import React, { ReactNode, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { getCurrentUserRole, hasPermission, normalizeRole, RolePermissions } from "../utils/rbac";

interface RoleProtectedRouteProps {
  permission: keyof RolePermissions;
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Route authorization guard that restricts access to subroutes based on user permissions.
 * Reactively listens to role updates so when an admin re-updates a user's role,
 * page access automatically changes and redirects unauthorized users immediately.
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  permission,
  children,
  redirectTo = "/dashboard/home",
}) => {
  const [role, setRole] = useState(getCurrentUserRole());

  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const freshRole = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setRole(freshRole);
    };

    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  const isAuth = localStorage.getItem("auth") === "true";
  if (!isAuth) {
    return <Navigate to="/login" replace />;
  }

  const isAllowed = hasPermission(permission, role);

  if (!isAllowed) {
    console.warn(
      `Access Denied: Role "${role}" lacks required permission "${permission}". Redirecting to ${redirectTo}`
    );
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
